import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Building2, Loader2 } from 'lucide-react';
import {
  joinBuildingByCode,
  joinFleetByCode,
  lookupBuildingByJoinCode,
  type PublicBuildingSummary,
} from '@/lib/api';
import {
  CURRENT_JOIN_AGREEMENT_VERSION,
  JOIN_AGREEMENT_PLACEHOLDER_TEXT,
} from '@evi/shared';
import { handleRequiresRelogin } from '@/lib/relogin';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import styles from './SwitchBuildingModal.module.css';

/**
 * Lazy-load AnimatedIcon for the cityscape hero. Same lazy chunk
 * the Profile and Rewards pages already pull in — Vite dedupes,
 * so this doesn't grow the lottie-react payload. The Suspense
 * fallback is a Lucide Building2 at the same hero size, so the
 * modal renders identically (just static) until the chunk lands.
 */
const AnimatedIcon = lazy(() => import('@/components/ui/AnimatedIcon'));

/** Hero illustration size. Mid your 120-160 range; fits the
 *  card's 412px internal width with comfortable margins, and
 *  leaves the existing title↔subtitle↔form spacing intact. */
const HERO_SIZE_PX = 140;

/**
 * Caller-supplied summary of the user's CURRENT building.
 *
 * Null = caller is still loading or the user has no resolvable
 * tenant. The modal treats null as the safest case (first-join
 * framing) so a slow /me/account fetch never lands the user in
 * a switch state with a placeholder name.
 *
 * isDefault = true means the user is in the seeded
 * 'ev-initiative' placeholder tenant (Tenant.is_default flag).
 * The modal renders the first-join state in this case: no prior
 * wallet sentence, "Join {target}?" copy, no current-building
 * subtext.
 *
 * isDefault = false means the user is already in an operator-
 * managed tenant. The modal renders the switch state: "Switch
 * to {target}?" copy, current-building subtext, wallet-stays-
 * here copy.
 */
export interface CurrentBuildingSummary {
  name: string;
  isDefault: boolean;
}

export interface SwitchBuildingModalProps {
  /** Pre-resolved code (from /login?join_code= or a URL flow).
   *  When provided, the modal opens straight to the confirmation
   *  state. */
  initialCode?: string;
  /** Optional pre-resolved building summary (Location-tier or
   *  Tenant-tier). When provided, the modal skips its own lookup. */
  initialBuilding?: PublicBuildingSummary | null;
  /**
   * The caller's current building. Drives the first-join vs
   * switch framing. See CurrentBuildingSummary for null /
   * isDefault semantics.
   */
  currentBuilding: CurrentBuildingSummary | null;
  onNavigate: (path: string) => void;
  onClose: () => void;
  /** Whether the modal allows manual code entry. When false the
   *  user can only confirm the pre-resolved code. */
  allowManualCodeEntry?: boolean;
}

// Helpers for the discriminated PublicBuildingSummary.
function buildingHeadline(b: PublicBuildingSummary): string {
  return b.kind === 'location'
    ? b.location.name
    : (b.tenant.display_name ?? b.tenant.name);
}
function buildingOperatorSubtitle(b: PublicBuildingSummary): string | null {
  return b.kind === 'location' ? (b.location.tenant_name ?? null) : null;
}
function buildingAddressLine(b: PublicBuildingSummary): string | null {
  if (b.kind !== 'location') return null;
  const a = b.location.address;
  return `${a.street}, ${a.city}, ${a.state}`;
}

/** Building codes are 6 chars (ambiguity-free alphabet), fleet invite
 *  codes are 8 chars (RFC 4648 base32). The one modal takes either and
 *  routes on length after normalisation — see handleManualSubmit. */
const BUILDING_CODE_LENGTH = 6;
const FLEET_CODE_LENGTH = 8;
const CODE_MAX_LENGTH = FLEET_CODE_LENGTH;

/**
 * Building join / switch modal — and, since the Account sheet collapsed
 * "Join a Building" + "Join a Fleet" into one row, the fleet code entry
 * too. Manual entry accepts a 6-char building code (→ the lookup /
 * confirm / legal-gate path below, unchanged) or an 8-char fleet
 * invite code (→ POST /fleets/join-by-code directly; fleets have no
 * building-style legal gate, matching /join-fleet). Any other length
 * gets one error naming both lengths.
 *
 * Two display states for the building path:
 *
 *   STATE A — first-join (currentBuilding null OR isDefault=true)
 *     The user has no prior real-tenant membership. Title reads
 *     "Join {target}?" and the body skips the wallet-stays-here
 *     copy entirely (no prior wallet exists).
 *
 *   STATE B — switch (currentBuilding.isDefault === false)
 *     The user is already in an operator-managed tenant. Title
 *     reads "Switch to {target}?" with the current building's
 *     name in the body and a wallet sentence framing the per-
 *     tenant balance honestly: it stays where it earned, not a
 *     "follows you" promise the schema can't keep.
 *
 * Both states render the SAME legal-acceptance gate inline:
 * a checkbox + scrollable card of placeholder legal text. The
 * Continue button is disabled until the checkbox is checked.
 * No "skip" affordance. The user accepts or backs out.
 *
 * On Accept, the request to /auth/join-building includes
 * agreement_version: CURRENT_JOIN_AGREEMENT_VERSION. Mismatch
 * with the server's version returns 409 with code
 * AGREEMENT_VERSION_OUTDATED so a future text bump will re-
 * prompt instead of silently passing stale acceptance.
 */
export function SwitchBuildingModal({
  initialCode,
  initialBuilding,
  currentBuilding,
  onNavigate,
  onClose,
  allowManualCodeEntry = false,
}: SwitchBuildingModalProps) {
  const { t } = useTranslation('modals');
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const [code, setCode] = useState(initialCode ?? '');
  const [building, setBuilding] = useState<PublicBuildingSummary | null>(
    initialBuilding ?? null,
  );
  const [phase, setPhase] = useState<'enter' | 'looking' | 'confirm' | 'switching' | 'joiningFleet'>(
    initialBuilding ? 'confirm' : initialCode ? 'looking' : 'enter',
  );
  const [errorMsg, setErrorMsg] = useState('');
  /**
   * Legal-gate state. Resets every time the modal re-mounts; we
   * intentionally don't persist this. A user who closes the
   * modal half-accepted starts the next session unchecked, which
   * matches the legal posture (acceptance is a deliberate act).
   */
  const [agreementChecked, setAgreementChecked] = useState(false);

  useEffect(() => {
    if (initialCode && !initialBuilding) {
      void resolveAndConfirm(initialCode);
    }
  }, []);

  const resolveAndConfirm = async (raw: string): Promise<void> => {
    setErrorMsg('');
    setPhase('looking');
    const result = await lookupBuildingByJoinCode(raw);
    if (!result) {
      setErrorMsg(t('switch.invalidCode'));
      setPhase('enter');
      return;
    }
    setBuilding(result);
    setPhase('confirm');
  };

  /**
   * Fleet branch. No lookup / confirm step: the fleet endpoint is the
   * lookup (a bad code 404s), and there is no building-style legal gate
   * for fleets — this is the same flow /join-fleet runs when a code is
   * typed there, down to the welcome toast and the /map landing.
   */
  const handleFleetJoin = async (raw: string): Promise<void> => {
    setErrorMsg('');
    setPhase('joiningFleet');
    try {
      const res = await joinFleetByCode(raw);
      await refreshUser();
      toast(
        t(res.already_member ? 'switch.fleetAlreadyThis' : 'switch.fleetWelcome', { fleetName: res.fleet_name }),
        { tone: 'accent' },
      );
      onClose();
      onNavigate('/map');
    } catch (err) {
      setErrorMsg(await mapFleetJoinError(err, t));
      setPhase('enter');
    }
  };

  const handleManualSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // The input already uppercases + strips non-alphanumerics on change,
    // so length is the only discriminator left: 6 → building, 8 → fleet.
    if (code.length === BUILDING_CODE_LENGTH) {
      void resolveAndConfirm(code);
      return;
    }
    if (code.length === FLEET_CODE_LENGTH) {
      void handleFleetJoin(code);
      return;
    }
    setErrorMsg(t('switch.codeLengthError'));
  };

  const handleConfirmSwitch = async (): Promise<void> => {
    if (!building) return;
    if (!agreementChecked) {
      // Defensive. The button is disabled when unchecked, but the
      // gate also lives here so a programmatic submit can't bypass
      // it.
      setErrorMsg(t('switch.acceptError'));
      return;
    }
    setPhase('switching');
    setErrorMsg('');
    try {
      const result = await joinBuildingByCode(code, CURRENT_JOIN_AGREEMENT_VERSION);
      if (result.requires_relogin) {
        handleRequiresRelogin(onNavigate, { tenantName: result.new_tenant.name });
        onClose();
        return;
      }
      // Pure intra-tenant Location move. Reload so the new
      // location_id propagates.
      window.location.reload();
      onClose();
    } catch (err) {
      setErrorMsg(await mapJoinError(err, buildingHeadline(building), t));
      setPhase('confirm');
    }
  };

  // SSR / pre-hydration guard for the portal (matches the pattern
  // BottomSheet and RedemptionSheet use). See
  // docs/tickets/css-containing-block-trap.md for why this modal
  // portals to body: its `position: fixed; inset: 0` overlay would
  // otherwise anchor to AppShell's `.pageTransition` wrapper rather
  // than the viewport, leaving the hero clipped on the larger
  // illustration.
  if (typeof document === 'undefined') return null;

  // Lucide fallback used in three places: the Suspense fallback
  // while the lazy AnimatedIcon chunk loads, the AnimatedIcon's own
  // `fallback` prop while building.json fetches, and the in-code
  // hero placeholder if the JSON file isn't present yet. All three
  // render the same large Building2 so the hero slot is visually
  // stable across every fallback state.
  const heroFallback = (
    <Building2
      size={HERO_SIZE_PX}
      strokeWidth={1.5}
      aria-hidden
    />
  );

  const tree = (
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-switch-title">
      <div className={styles.card}>
        {/* Cityscape hero — replaces the prior 56px circular badge.
            Continuous loop (decorative motion); tap is a no-op so
            the hero doesn't compete with the form's interactive
            elements for the user's attention. */}
        <div className={styles.hero} aria-hidden>
          <Suspense fallback={heroFallback}>
            <AnimatedIcon
              src="/icons/building.json"
              fallback={heroFallback}
              size={HERO_SIZE_PX}
              playOnMount
              playOnTap={false}
              loop
            />
          </Suspense>
        </div>

        {phase === 'enter' && (
          <ManualEntryBody
            code={code}
            setCode={setCode}
            errorMsg={errorMsg}
            onSubmit={handleManualSubmit}
            onCancel={onClose}
            allowManualCodeEntry={allowManualCodeEntry}
          />
        )}

        {phase === 'looking' && (
          <div className={styles.lookingBody}>
            <h2 id="evi-switch-title" className={styles.title}>{t('switch.looking')}</h2>
            <Loader2 size={18} className={styles.spin} />
          </div>
        )}

        {phase === 'joiningFleet' && (
          <div className={styles.lookingBody}>
            <h2 id="evi-switch-title" className={styles.title}>{t('switch.joiningFleet')}</h2>
            <Loader2 size={18} className={styles.spin} />
          </div>
        )}

        {(phase === 'confirm' || phase === 'switching') && building && (
          <ConfirmBody
            building={building}
            currentBuilding={currentBuilding}
            errorMsg={errorMsg}
            switching={phase === 'switching'}
            agreementChecked={agreementChecked}
            onAgreementToggle={setAgreementChecked}
            onConfirm={handleConfirmSwitch}
            onCancel={onClose}
            onBackToEntry={allowManualCodeEntry ? () => setPhase('enter') : undefined}
          />
        )}
      </div>
    </div>
  );

  return createPortal(tree, document.body);
}

function ManualEntryBody({
  code, setCode, errorMsg, onSubmit, onCancel, allowManualCodeEntry,
}: {
  code: string;
  setCode: (c: string) => void;
  errorMsg: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  /** True from the Account sheet's single "Join a building or fleet" row,
   *  where either code kind is welcome; false on the pre-resolved /join
   *  and /login?join_code= flows, which are building-only and only reach
   *  this body when their code failed to resolve. */
  allowManualCodeEntry: boolean;
}): ReactNode {
  const { t } = useTranslation('modals');
  const submittable = code.length === BUILDING_CODE_LENGTH || code.length === FLEET_CODE_LENGTH;
  return (
    <>
      <h2 id="evi-switch-title" className={styles.title}>
        {allowManualCodeEntry ? t('switch.codeTitle') : t('switch.enterTitle')}
      </h2>
      <p className={styles.subtitle}>
        {allowManualCodeEntry ? t('switch.codeSubtitle') : t('switch.manualSubtitle')}
      </p>
      <form onSubmit={onSubmit} className={styles.form} noValidate>
        {errorMsg && <div className={styles.error} role="alert">{errorMsg}</div>}
        <input
          className={styles.codeInput}
          type="text"
          value={code}
          // Uppercase + strip everything that isn't a letter or digit (spaces,
          // hyphens from a dictated "A7K2-P9X4") and cap at the fleet length,
          // so the submit handler can dispatch on length alone. The old 6-char
          // cap silently truncated a pasted fleet code into a bad building code.
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_MAX_LENGTH))}
          placeholder="ABC123"
          maxLength={CODE_MAX_LENGTH}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          aria-label={allowManualCodeEntry ? t('switch.codeAriaAny') : t('switch.codeAria')}
        />
        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            {t('switch.cancel')}
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={!submittable}
          >
            {code.length === FLEET_CODE_LENGTH ? t('switch.joinFleet') : t('switch.lookup')}
          </button>
        </div>
      </form>
    </>
  );
}

function ConfirmBody({
  building, currentBuilding, errorMsg, switching,
  agreementChecked, onAgreementToggle,
  onConfirm, onCancel, onBackToEntry,
}: {
  building: PublicBuildingSummary;
  currentBuilding: CurrentBuildingSummary | null;
  errorMsg: string;
  switching: boolean;
  agreementChecked: boolean;
  onAgreementToggle: (checked: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onBackToEntry?: () => void;
}): ReactNode {
  const { t } = useTranslation('modals');
  const targetName = buildingHeadline(building);
  const operatorSubtitle = buildingOperatorSubtitle(building);
  const addressLine = buildingAddressLine(building);

  // First-join state when the user has no current building OR the
  // current one is the placeholder/default tenant. Both cases mean
  // "no prior real-tenant wallet exists" so the wallet sentence
  // is omitted entirely.
  const isFirstJoin = !currentBuilding || currentBuilding.isDefault;

  return (
    <>
      <h2 id="evi-switch-title" className={styles.title}>
        {isFirstJoin
          ? t('switch.joinTitle', { target: targetName })
          : t('switch.switchTitle', { target: targetName })}
      </h2>
      {(operatorSubtitle || addressLine) && (
        <p className={styles.subtitle}>
          {[operatorSubtitle, addressLine].filter(Boolean).join(' · ')}
        </p>
      )}
      {isFirstJoin ? (
        <p className={styles.subtitle}>
          {t('switch.firstJoinBody', { target: targetName })}
        </p>
      ) : (
        <p className={styles.subtitle}>
          {t('switch.switchBodyPre')}<strong>{currentBuilding!.name}</strong>{t('switch.switchBodyPost', { target: targetName })}
        </p>
      )}

      {/* Disclosure card. Both states explain the platform-vs-
          operator role; only the switch state mentions the
          per-tenant wallet (first-join has no prior wallet). */}
      <div className={styles.walletNote}>
        {isFirstJoin
          ? t('switch.platformNoteFirst', { operator: operatorSubtitle ?? targetName })
          : t('switch.walletNoteSwitch', { current: currentBuilding!.name, target: targetName })}
      </div>

      {/* Legal acceptance gate. Inline rather than a stacked
          modal-over-modal because the agreement is part of the
          decision the user is making in this confirmation. The
          DisclaimerModal pattern is for first-encounter ack flows
          on a different surface; this is the same surface. */}
      <div className={styles.legalGate}>
        <p className={styles.legalGateTitle}>{t('switch.beforeContinuing')}</p>
        <div className={styles.legalText} role="region" aria-label={t('switch.termsAria')}>
          {JOIN_AGREEMENT_PLACEHOLDER_TEXT}
        </div>
        <label className={styles.legalCheckbox}>
          <input
            type="checkbox"
            checked={agreementChecked}
            onChange={(e) => onAgreementToggle(e.target.checked)}
            disabled={switching}
          />
          <span>I&rsquo;ve read and agree to the terms above.</span>
        </label>
      </div>

      {errorMsg && <div className={styles.error} role="alert">{errorMsg}</div>}

      <div className={styles.actions}>
        {onBackToEntry ? (
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onBackToEntry}
            disabled={switching}
          >
            {t('switch.differentCode')}
          </button>
        ) : (
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={switching}
          >
            {t('switch.cancel')}
          </button>
        )}
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={onConfirm}
          disabled={switching || !agreementChecked}
        >
          {switching
            ? <><Loader2 size={14} className={styles.spin} /> {isFirstJoin ? t('switch.joining') : t('switch.switching')}</>
            : (isFirstJoin ? t('switch.join') : t('switch.switchAction'))}
        </button>
      </div>
    </>
  );
}

/** Map structured error codes from POST /auth/join-building into
 *  user-friendly inline copy. */
async function mapJoinError(
  err: unknown,
  targetName: string,
  t: ReturnType<typeof useTranslation>['t'],
): Promise<string> {
  const response = (err as { response?: Response }).response;
  if (!response) {
    return err instanceof Error ? err.message : t('switch.errors.tryAgain');
  }
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    const code = body.error?.code;
    switch (code) {
      case 'ALREADY_IN_TENANT':
        return t('switch.errors.alreadyMember', { target: targetName });
      case 'EMAIL_EXISTS_AT_TARGET':
        return body.error?.message
          ?? t('switch.errors.emailExists', { target: targetName });
      case 'INVALID_BUILDING_CODE':
        return t('switch.invalidCode');
      case 'AGREEMENT_VERSION_OUTDATED':
        // The server's version moved while the modal was open.
        // Refreshing the page reloads the constant from the bundle
        // so the next acceptance carries the right version.
        return t('switch.errors.termsUpdated');
      default:
        return body.error?.message ?? t('switch.errors.tryAgain');
    }
  } catch {
    return t('switch.errors.tryAgain');
  }
}

/** Map structured error codes from POST /fleets/join-by-code into inline
 *  copy. Same three codes JoinFleetPage's form distinguishes; anything
 *  else falls back to the server message, then the generic line. */
async function mapFleetJoinError(
  err: unknown,
  t: ReturnType<typeof useTranslation>['t'],
): Promise<string> {
  const response = (err as { response?: Response }).response;
  if (!response) {
    return err instanceof Error ? err.message : t('switch.fleetGenericError');
  }
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    switch (body.error?.code) {
      case 'INVITE_CODE_INVALID':
        return t('switch.fleetInvalidCode');
      case 'DRIVER_ALREADY_IN_FLEET':
        return t('switch.fleetAlreadyIn');
      case 'CROSS_TENANT_INVITE':
        // The fleet lives in another building's tenant; the fix is a
        // building code in this same field, then the fleet code again.
        return t('switch.fleetCrossTenant');
      default:
        return body.error?.message ?? t('switch.fleetGenericError');
    }
  } catch {
    return t('switch.fleetGenericError');
  }
}
